import React, {useEffect, useState, useRef} from 'react';
import {Form, Input, Select, TimePicker} from 'antd';
import { passwordPolicyRule } from 'utils/passwordValidator';
import EditableDrawer from 'components/EditableDrawer';
import {FormInstance} from 'antd/es/form';
import {CountryCodeAndPhoneNumber, PhoneInput} from 'components/PhoneInput';
import {useTmsVehicleOptions} from 'hooks/useTmsVehicleOptions';
import {useWarehouseContext} from 'contexts/WarehouseContext';
import {ROLE_OPTIONS} from 'constants/roleOptions';
import dayjs from 'dayjs';
import {AccountFormData} from 'hooks/useAccountApis';
import { useTranslation } from 'react-i18next';

interface AccountFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: AccountFormData) => Promise<void>;
    account?: AccountFormData | null; // If account data is provided, it's edit mode, otherwise it's create mode
    mode: 'create' | 'edit'; // Explicitly specify mode
    carrierOptions: { label: string; value: number; status?: number }[];
}

const AccountFormDrawer: React.FC<AccountFormDrawerProps> = ({
                                                                 visible,
                                                                 onClose,
                                                                 onSubmit,
                                                                 account,
                                                                 mode,
                                                                 carrierOptions
                                                             }) => {
    const { t } = useTranslation();
    const drawerTitle = mode === 'create' ? t('pickup.account.pageDetails.newPersonnel') : t('pickup.account.pageDetails.personnelDetails');
    const [form] = Form.useForm();
    const [filteredCarrierOptions, setFilteredCarrierOptions] = useState<{ label: string; value: number; status?: number }[]>([]);
    useEffect(() => {
        if (visible) {
            form.resetFields();
            
            // 过滤承运商选项：如果status为0，则只保留与当前表单carrierId值一致的选项
            const filteredCarriers = carrierOptions.filter(option => {
                if (option.status === 0) {
                    const currentCarrierId = account?.carrierId ? Number(account.carrierId) : undefined;
                    return currentCarrierId !== undefined && option.value === currentCarrierId;
                }
                return true;
            });
            setFilteredCarrierOptions(filteredCarriers);

            if (account) {
                // 确保 responsibleZipcodes 字段总是有值，并处理时间字段转换
                const accountWithDefaults = {
                    ...account,
                    carrierId: account.carrierId ? Number(account.carrierId) : undefined,
                    vehicleId: account.vehicleId ? Number(account.vehicleId) : undefined,
                    responsibleZipcodes: account.responsibleZipcodes || [],
                    // 将字符串时间转换为 Dayjs 时间范围
                    workHours: account.workStartTime && account.workEndTime ?
                        [dayjs(account.workStartTime, 'HH:mm'), dayjs(account.workEndTime, 'HH:mm')] :
                        [dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')]
                };
                form.setFieldsValue(accountWithDefaults);
                form.setFieldsValue({
                    role: account.role,
                });
            } else {
                form.setFieldsValue({
                    role: 'TMS_DRIVER',
                    status: 1,
                    isOnDuty: true,
                    phone: {
                        countryCode: 1,
                        phoneNumber: '',
                    },
                });
            }
        }
    }, [account, form, visible, carrierOptions]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();

            // 将时间范围转换为独立的开始和结束时间
            const processedValues = {
                ...values,
            };

            await onSubmit(processedValues);
        } catch (error) {
            console.error('error', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmText={isSubmitting ? t('tms.account.messages.processing') as string : t('tms.account.pageDetails.confirm') as string}
            cancelText={t('tms.account.pageDetails.cancel') as string}
            confirmDisabled={isSubmitting}
            width={600}
        >
            <AccountForm
                form={form}
                mode={mode}
                filteredCarrierOptions={filteredCarrierOptions}
                initialCarrierId={account?.carrierId ? Number(account.carrierId) : undefined}
            />
        </EditableDrawer>
    );
};

interface AccountFormProps {
    mode: 'create' | 'edit';
    form: FormInstance<AccountFormData>;
    filteredCarrierOptions: { label: string; value: number; status?: number }[];
    initialCarrierId?: number;
}

const AccountForm = ({mode, form, filteredCarrierOptions, initialCarrierId}: AccountFormProps) => {
    const { t } = useTranslation();

    const STATUS_OPTIONS = [
        {value: 1, label: t('enum.activeStatus.active')},
        {value: 0, label: t('enum.activeStatus.suspended')}
    ];
    const onFinish = (values: any) => {
        console.log(values);
    }

    const role = Form.useWatch('role', form);
    const watchedCarrierId = Form.useWatch('carrierId', form);
    // 编辑详情打开时，form 要先 resetFields() 再 setFieldsValue()，中间会有一帧 watchedCarrierId 为 undefined；
    // 用 initialCarrierId（来自 account，渲染时同步可得）兜底，避免车辆列表按"未选承运商"先查一次再被正确的第二次查询覆盖
    const carrierId = watchedCarrierId !== undefined ? watchedCarrierId : initialCarrierId;
    const {currentWarehouse} = useWarehouseContext();
    const {vehicleOptions, loading: vehicleLoading} = useTmsVehicleOptions({
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
        carrierId: carrierId ? Number(carrierId) : undefined
    });
    const vehicleId = Form.useWatch('vehicleId', form);
    const previousCarrierIdRef = useRef<number | undefined>(undefined);
    const isInitialMountRef = useRef(true);
    
    // 当承运商变化时，清空车辆字段（但不在初始化时清空）
    useEffect(() => {
        const currentCarrierId = carrierId ? Number(carrierId) : undefined;
        
        if (isInitialMountRef.current) {
            // 初始化完成，记录初始承运商ID，标记为非初始化阶段
            previousCarrierIdRef.current = currentCarrierId;
            isInitialMountRef.current = false;
            return;
        }
        
        const previousCarrierId = previousCarrierIdRef.current;
        
        // 如果承运商发生了变化（且不是从undefined变为有值），清空车辆字段
        if (currentCarrierId !== previousCarrierId && previousCarrierId !== undefined) {
            form.setFieldsValue({ vehicleId: undefined });
        }
        
        previousCarrierIdRef.current = currentCarrierId;
    }, [carrierId, form]);
    
    const filteredVehicleOptions = (vehicleOptions.filter(option => option.value !== vehicleId)||[]).map(option => ({
        value: +option.value,
        label: option.label
    }));

    return (
        <Form
            form={form}
            onFinish={onFinish}
            labelCol={{span: 6}}
            wrapperCol={{span: 18}}
        >
            {/* keep ID in form state for edit submit, but do not display */}
            <Form.Item name="id" hidden>
                <Input type="hidden" />
            </Form.Item>
            <Form.Item label={t('tms.account.pageDetails.phone')}
                       name="phone"
                       rules={[
                           {
                               validator: (_, value: CountryCodeAndPhoneNumber) => {
                                   if (!value || !value.phoneNumber) {
                                       return Promise.reject(new Error(t('tms.account.messages.phoneNumberRequired') as string));
                                   }

                                   // 验证手机号只包含数字
                                   const phoneRegex = /^\d+$/;
                                   if (!phoneRegex.test(value.phoneNumber)) {
                                       return Promise.reject(new Error(t('tms.account.messages.phoneNumberDigitsOnly') as string));
                                   }

                                   return Promise.resolve();
                               }
                           }
                       ]}
            >
                <PhoneInput/>
            </Form.Item>
            <Form.Item
                label={t('tms.account.pageDetails.password')}
                name="password"
                validateTrigger="onBlur"
                rules={mode === 'create'
                    ? [{required: true, message: t('tms.account.messages.pleaseInputPassword') as string}, passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
                    : [passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]}
            >
                <Input.Password placeholder={t('accountSecurity.passwordHint') as string}/>
            </Form.Item>
            <Form.Item
                label={t('tms.account.pageDetails.name')}
                name="name"
                rules={[{required: true, message: t('tms.account.messages.pleaseInputName') as string}]}
            >
                <Input placeholder={t('tms.account.placeholders.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item
                label={t('tms.account.pageDetails.email')}
                name="email"
                rules={[{type: 'email', message: t('tms.account.messages.pleaseInputValidEmail') as string}]}
            >
                <Input placeholder={t('tms.account.placeholders.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item label={t('tms.account.pageDetails.personnelStatus')} name="status">
                <Select
                    options={STATUS_OPTIONS}
                    placeholder={t('tms.account.pageDetails.select') as string}
                />
            </Form.Item>
            <Form.Item
                label={t('tms.account.pageDetails.role')}
                name="role"
                rules={[{required: true, message: t('tms.account.messages.pleaseSelectRole') as string}]}
            >
                <Select
                    disabled={true}
                    options={ROLE_OPTIONS}
                    placeholder="select"
                />
            </Form.Item>
            {role === 'TMS_DRIVER' && (
                <>
                    <Form.Item 
                        label={t('tms.vehicle.pageDetails.carrier')} 
                        name="carrierId"
                        rules={[{required: true, message: t('tms.vehicle.messages.pleaseSelectCarrier') as string}]}
                    >
                        <Select
                            options={filteredCarrierOptions}
                            placeholder={t('tms.vehicle.placeholders.selectCarrier') as string}
                        />
                    </Form.Item>
                    <Form.Item 
                        label={t('tms.account.pageDetails.vehicle')}
                        name="vehicleId"
                    >
                        <Select
                            loading={vehicleLoading}
                            placeholder={t('tms.account.placeholders.selectVehicleOptional') as string}
                            disabled={!carrierId || filteredVehicleOptions.length === 0}
                            allowClear
                            optionLabelProp="label"
                            options={filteredVehicleOptions}
                        />
                    </Form.Item>
                </>
            )}
        </Form>
    )
};

export default AccountFormDrawer; 